import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useUserRole } from "@/hooks/useUserRole";
import { supabase } from "@/integrations/supabase/client";
import Navbar from "@/components/Navbar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import PharmacySidebar from "@/components/sidebar/PharmacySidebar";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { toast } from "sonner";
import { ChevronRight as BreadcrumbChevron } from "lucide-react";
import {
  Pill,
  Package,
  Clock,
  CheckCircle,
  Truck,
  Search,
  Building2,
  RefreshCw,
  PanelLeft,
  FlaskConical,
} from "lucide-react";
import { format } from "date-fns";

interface Prescription {
  id: string;
  peptide_name: string;
  dosage: string;
  frequency: string;
  quantity: string;
  route: string;
  refills: number;
  instructions: string | null;
  status: string;
  patient_id: string;
  provider_id: string;
  pharmacy_id: string;
  created_at: string;
  updated_at: string;
  sent_at: string | null;
}

interface PharmacyInfo {
  id: string;
  name: string;
}

const STATUS_CONFIG: Record<string, { label: string; variant: "default" | "secondary" | "outline" | "destructive"; icon: any }> = {
  sent: { label: "Received", variant: "default", icon: Clock },
  filling: { label: "Filling", variant: "secondary", icon: Package },
  filled: { label: "Filled", variant: "outline", icon: CheckCircle },
  shipped: { label: "Shipped", variant: "default", icon: Truck },
  delivered: { label: "Delivered", variant: "default", icon: CheckCircle },
};

const PHARMACY_STATUSES = ["sent", "filling", "filled", "shipped", "delivered"];

const PharmacyDashboard = () => {
  const { user, loading: authLoading } = useAuth();
  const { isPharmacy, isAdmin, loading: roleLoading } = useUserRole();
  const [activeTab, setActiveTab] = useState("overview");
  const [prescriptions, setPrescriptions] = useState<Prescription[]>([]);
  const [pharmacy, setPharmacy] = useState<PharmacyInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [patientProfiles, setPatientProfiles] = useState<Record<string, { first_name: string | null; last_name: string | null }>>({});
  const [providerProfiles, setProviderProfiles] = useState<Record<string, { first_name: string | null; last_name: string | null; title_prefix: string | null }>>({});

  const fetchData = async () => {
    if (!user) return;
    setLoading(true);

    try {
      // Get pharmacy info via pharmacy_staff
      const { data: staffData } = await supabase
        .from("pharmacy_staff")
        .select("pharmacy_id")
        .eq("user_id", user.id)
        .limit(1)
        .single();

      if (staffData) {
        const { data: pharmacyData } = await supabase
          .from("partner_pharmacies")
          .select("id, name")
          .eq("id", staffData.pharmacy_id)
          .single();

        if (pharmacyData) setPharmacy(pharmacyData);
      }

      // Fetch prescriptions (RLS handles scoping)
      const { data: rxData, error } = await supabase
        .from("prescriptions")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setPrescriptions(rxData || []);

      // Fetch patient & provider profiles for display
      if (rxData && rxData.length > 0) {
        const patientIds = [...new Set(rxData.map((r) => r.patient_id))];
        const providerIds = [...new Set(rxData.map((r) => r.provider_id))];

        // We can't see patient profiles as pharmacy, but providers are visible
        const { data: provProfiles } = await supabase
          .from("profiles")
          .select("user_id, first_name, last_name, title_prefix")
          .in("user_id", providerIds);

        if (provProfiles) {
          const map: Record<string, any> = {};
          provProfiles.forEach((p) => { map[p.user_id] = p; });
          setProviderProfiles(map);
        }

        // Try fetching patient profiles (may be limited by RLS)
        const { data: patProfiles } = await supabase
          .from("profiles")
          .select("user_id, first_name, last_name")
          .in("user_id", patientIds);

        if (patProfiles) {
          const map: Record<string, any> = {};
          patProfiles.forEach((p) => { map[p.user_id] = p; });
          setPatientProfiles(map);
        }
      }
    } catch (err) {
      console.error("Error fetching pharmacy data:", err);
      toast.error("Failed to load pharmacy data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authLoading && !roleLoading && user && (isPharmacy || isAdmin)) {
      fetchData();
    }
  }, [user, authLoading, roleLoading, isPharmacy, isAdmin]);

  // Real-time subscription for prescription changes
  useEffect(() => {
    if (!user || authLoading || roleLoading || (!isPharmacy && !isAdmin)) return;

    const channel = supabase
      .channel("pharmacy-prescriptions")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "prescriptions" },
        (payload) => {
          const rx = payload.new as Prescription;
          toast.info("New Prescription Received", {
            description: `${rx.peptide_name} — ${rx.dosage}, ${rx.quantity}`,
            duration: 8000,
          });
          fetchData();
        }
      )
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "prescriptions" },
        () => {
          fetchData();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, authLoading, roleLoading, isPharmacy, isAdmin]);

  if (authLoading || roleLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;
  if (!isPharmacy && !isAdmin) return <Navigate to="/dashboard" replace />;

  const updatePrescriptionStatus = async (rxId: string, newStatus: string) => {
    setUpdatingId(rxId);
    try {
      const { error } = await supabase
        .from("prescriptions")
        .update({ status: newStatus })
        .eq("id", rxId);

      if (error) throw error;
      setPrescriptions((prev) =>
        prev.map((rx) => (rx.id === rxId ? { ...rx, status: newStatus } : rx))
      );
      toast.success(`Prescription marked as ${STATUS_CONFIG[newStatus]?.label || newStatus}`);
    } catch (err) {
      console.error("Error updating prescription:", err);
      toast.error("Failed to update status");
    } finally {
      setUpdatingId(null);
    }
  };

  const getNextStatus = (currentStatus: string): string | null => {
    const idx = PHARMACY_STATUSES.indexOf(currentStatus);
    if (idx === -1 || idx >= PHARMACY_STATUSES.length - 1) return null;
    return PHARMACY_STATUSES[idx + 1];
  };

  const getPatientDisplay = (patientId: string) => {
    const p = patientProfiles[patientId];
    if (p?.first_name) return `${p.first_name} ${p.last_name || ""}`.trim();
    return patientId.slice(0, 8) + "…";
  };

  const getProviderDisplay = (providerId: string) => {
    const p = providerProfiles[providerId];
    if (p?.first_name) {
      const prefix = p.title_prefix ? `${p.title_prefix} ` : "";
      return `${prefix}${p.first_name} ${p.last_name || ""}`.trim();
    }
    return providerId.slice(0, 8) + "…";
  };

  const filteredPrescriptions = prescriptions.filter((rx) => {
    const matchesStatus = statusFilter === "all" || rx.status === statusFilter;
    const matchesSearch =
      !searchTerm ||
      rx.peptide_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      rx.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      getPatientDisplay(rx.patient_id).toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  // Stats
  const pending = prescriptions.filter((rx) => rx.status === "sent").length;
  const filling = prescriptions.filter((rx) => rx.status === "filling").length;
  const filled = prescriptions.filter((rx) => rx.status === "filled").length;
  const shipped = prescriptions.filter((rx) => rx.status === "shipped" || rx.status === "delivered").length;

  const TAB_LABELS: Record<string, string> = {
    overview: "Overview",
    prescriptions: "Prescriptions",
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="pt-16">
        <SidebarProvider>
          <div className="min-h-[calc(100vh-4rem)] flex w-full">
            <PharmacySidebar activeTab={activeTab} onNavigateToTab={setActiveTab} pharmacyName={pharmacy?.name} />
            <main className="flex-1 overflow-auto">
              <div className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <SidebarTrigger className="shrink-0">
                    <PanelLeft size={18} />
                  </SidebarTrigger>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Building2 size={18} className="text-primary" />
                    </div>
                    <div>
                      <h1 className="font-display text-2xl text-foreground leading-tight">Pharmacy Dashboard</h1>
                      {pharmacy && <p className="text-muted-foreground text-sm">{pharmacy.name}</p>}
                      {activeTab !== "overview" && (
                        <nav className="flex items-center gap-1.5 text-xs mt-0.5">
                          <button
                            onClick={() => setActiveTab("overview")}
                            className="text-muted-foreground hover:text-primary transition-colors font-medium"
                          >
                            Overview
                          </button>
                          <BreadcrumbChevron size={12} className="text-muted-foreground/50" />
                          <span className="text-foreground font-medium">
                            {TAB_LABELS[activeTab] || activeTab}
                          </span>
                        </nav>
                      )}
                    </div>
                  </div>
                  <div className="ml-auto flex items-center gap-2">
                    <Button asChild variant="outline" size="sm" className="gap-2">
                      <Link to="/therapies">
                        <FlaskConical size={14} /> Therapy Library
                      </Link>
                    </Button>
                    <Button variant="outline" className="gap-2" size="sm" onClick={fetchData}>
                      <RefreshCw size={14} /> Refresh
                    </Button>
                  </div>
                </div>

                {activeTab === "overview" && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                    <Card className="border-border">
                      <CardContent className="pt-5 pb-4">
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-lg bg-amber-500/10">
                            <Clock size={20} className="text-amber-500" />
                          </div>
                          <div>
                            <p className="text-2xl font-bold text-foreground">{pending}</p>
                            <p className="text-xs text-muted-foreground">Received</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="border-border">
                      <CardContent className="pt-5 pb-4">
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-lg bg-blue-500/10">
                            <Package size={20} className="text-blue-500" />
                          </div>
                          <div>
                            <p className="text-2xl font-bold text-foreground">{filling}</p>
                            <p className="text-xs text-muted-foreground">Filling</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="border-border">
                      <CardContent className="pt-5 pb-4">
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-lg bg-emerald-500/10">
                            <CheckCircle size={20} className="text-emerald-500" />
                          </div>
                          <div>
                            <p className="text-2xl font-bold text-foreground">{filled}</p>
                            <p className="text-xs text-muted-foreground">Filled</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="border-border">
                      <CardContent className="pt-5 pb-4">
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-lg bg-primary/10">
                            <Truck size={20} className="text-primary" />
                          </div>
                          <div>
                            <p className="text-2xl font-bold text-foreground">{shipped}</p>
                            <p className="text-xs text-muted-foreground">Shipped / Delivered</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {(activeTab === "overview" || activeTab === "prescriptions") && (
                  <>
                    {/* Filters */}
                    <Card className="border-border mb-6">
                      <CardContent className="pt-5 pb-4">
                        <div className="flex flex-col sm:flex-row gap-3">
                          <div className="relative flex-1">
                            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                            <Input
                              placeholder="Search by medication, patient, or Rx ID…"
                              value={searchTerm}
                              onChange={(e) => setSearchTerm(e.target.value)}
                              className="pl-9"
                            />
                          </div>
                          <Select value={statusFilter} onValueChange={setStatusFilter}>
                            <SelectTrigger className="w-full sm:w-44">
                              <SelectValue placeholder="Filter by status" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">All Statuses</SelectItem>
                              <SelectItem value="sent">Received</SelectItem>
                              <SelectItem value="filling">Filling</SelectItem>
                              <SelectItem value="filled">Filled</SelectItem>
                              <SelectItem value="shipped">Shipped</SelectItem>
                              <SelectItem value="delivered">Delivered</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Prescriptions Table */}
                    <Card className="border-border">
                      <CardHeader className="pb-3">
                        <CardTitle className="font-display text-lg flex items-center gap-2">
                          <Pill size={18} /> Prescriptions ({filteredPrescriptions.length})
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="p-0">
                        {loading ? (
                          <div className="flex items-center justify-center py-16">
                            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                          </div>
                        ) : filteredPrescriptions.length === 0 ? (
                          <div className="text-center py-16 text-muted-foreground">
                            <Pill size={40} className="mx-auto mb-3 opacity-30" />
                            <p className="font-medium">No prescriptions found</p>
                            <p className="text-sm mt-1">
                              {statusFilter !== "all"
                                ? "Try changing the status filter."
                                : "Prescriptions routed to your pharmacy will appear here."}
                            </p>
                          </div>
                        ) : (
                          <div className="overflow-x-auto">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Medication</TableHead>
                                  <TableHead>Patient</TableHead>
                                  <TableHead>Prescriber</TableHead>
                                  <TableHead>Dosage</TableHead>
                                  <TableHead>Qty / Refills</TableHead>
                                  <TableHead>Status</TableHead>
                                  <TableHead>Received</TableHead>
                                  <TableHead className="text-right">Action</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {filteredPrescriptions.map((rx) => {
                                  const statusInfo = STATUS_CONFIG[rx.status];
                                  const nextStatus = getNextStatus(rx.status);
                                  const StatusIcon = statusInfo?.icon || Clock;

                                  return (
                                    <TableRow key={rx.id}>
                                      <TableCell>
                                        <div className="font-medium text-foreground">{rx.peptide_name}</div>
                                        <div className="text-xs text-muted-foreground">{rx.route}</div>
                                      </TableCell>
                                      <TableCell className="text-sm">{getPatientDisplay(rx.patient_id)}</TableCell>
                                      <TableCell className="text-sm">{getProviderDisplay(rx.provider_id)}</TableCell>
                                      <TableCell>
                                        <div className="text-sm">{rx.dosage}</div>
                                        <div className="text-xs text-muted-foreground">{rx.frequency}</div>
                                      </TableCell>
                                      <TableCell className="text-sm">
                                        {rx.quantity} / {rx.refills} refills
                                      </TableCell>
                                      <TableCell>
                                        <Badge variant={statusInfo?.variant || "secondary"} className="gap-1">
                                          <StatusIcon size={12} />
                                          {statusInfo?.label || rx.status}
                                        </Badge>
                                      </TableCell>
                                      <TableCell className="text-sm text-muted-foreground">
                                        {rx.sent_at
                                          ? format(new Date(rx.sent_at), "MMM d, yyyy")
                                          : format(new Date(rx.created_at), "MMM d, yyyy")}
                                      </TableCell>
                                      <TableCell className="text-right">
                                        {nextStatus ? (
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            disabled={updatingId === rx.id}
                                            onClick={() => updatePrescriptionStatus(rx.id, nextStatus)}
                                            className="gap-1.5"
                                          >
                                            {updatingId === rx.id ? (
                                              <RefreshCw size={12} className="animate-spin" />
                                            ) : (
                                              STATUS_CONFIG[nextStatus]?.icon && (
                                                <span><StatusIcon size={12} /></span>
                                              )
                                            )}
                                            Mark {STATUS_CONFIG[nextStatus]?.label || nextStatus}
                                          </Button>
                                        ) : (
                                          <span className="text-xs text-muted-foreground">Complete</span>
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </>
                )}
              </div>
            </main>
          </div>
        </SidebarProvider>
      </div>
    </div>
  );
};

export default PharmacyDashboard;
